import { useCallback, useState } from 'react';

import { Tooltip } from '@mantine/core';

import { type AnyForm, NO_REMOUNT } from './validatedFields.js';
import classes from './belowLimitToggle.module.css';

// Liest einen verschachtelten Formularwert anhand eines Punktpfads -- dieselbe Logik wie das
// (nicht exportierte) getPath in validatedFields.tsx. Lokal dupliziert statt importiert, exakte
// Praezedenz: KilnForm.tsx Zeilen 105-115 (getNestedValue) dupliziert dieselben vier Zeilen aus
// demselben Grund -- getPath bleibt modulprivat in validatedFields.tsx.
function getNestedValue(values: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, values);
}

interface BelowLimitToggleProps {
  form: AnyForm;
  field: string;
  disabled?: boolean;
}

/**
 * Kippknopf fuer die Bestimmungsgrenze eines Zahlenfelds (Sketch 014, Variante F4;
 * D-07/D-08/D-09/D-10/D-20). Rendert als `leftSection` innerhalb von `ValidatedNumberInput` --
 * siehe dort fuer die Vorrangregel gegenueber dem Fehlersymbol.
 *
 * Der Knopf zeigt, was gerade gilt: "=" fuer einen gemessenen Wert, "<" fuer einen Wert unter
 * der Bestimmungsgrenze. Das Zeichen IST die Aussage, nicht bloss eine Zustandsmarke -- "= 0,42"
 * bzw. "< 0,0008" liest sich als ein Ausdruck.
 *
 * Zwei Vorlaeufer, beide abgeloest: Sketch 007 stellte "=" und "<" als *zwei* Knoepfe
 * nebeneinander, von denen einer dauerhaft dunkel markiert war -- Rauschen fuer den Normalfall.
 * Sketch 011 machte daraus einen einzelnen "<"-Knopf, dessen Zustand allein ein
 * Helligkeitssprung trug; der war auf 28 px zu leise, um beim Ueberfliegen der 26 Messfelder
 * aufzufallen. F4 behaelt den einen Knopf und laesst statt der Helligkeit das Zeichen wechseln.
 *
 * **Zwischen den Zustaenden aendert sich ausschliesslich das Zeichen** -- keine Flaeche, keine
 * Farbe, kein Schriftschnitt (Vorgabe aus dem Gespraech vom 2026-09-03). Das CSS fuehrt deshalb
 * bewusst keine `.active`-Regel; siehe den Kopfkommentar in `belowLimitToggle.module.css`.
 * Maschinenlesbar bleibt der Zustand an `aria-pressed`, dazu nennt ihn der Tooltip im Klartext.
 *
 * Isolierte Blattkomponente nach dem Muster von `MeasurementTitle`
 * (`KilnMeasurementCard.tsx`, Zeilen 63-122): das Formular ist uncontrolled, `NO_REMOUNT`
 * unterdrueckt den Feld-Key-Bump beim Schreiben, also loest `form.setFieldValue(..., NO_REMOUNT)`
 * kein Re-Render aus. Der Umschalter braucht deshalb lokalen State plus `form.watch`, um seinen
 * eigenen Zustandswechsel ueberhaupt anzuzeigen.
 *
 * `form.watch` gehoert direkt in den Render-Body, NICHT in einen zusaetzlich umschliessenden
 * Effekt-Hook: Mantine registriert den Watcher intern bereits selbst ueber einen eigenen
 * Effekt-Hook und meldet ihn im Cleanup wieder ab (siehe `MeasurementTitle`-Kommentar). Ein
 * weiterer Effekt-Hook darum herum waere ein Hook-Aufruf innerhalb eines Callbacks und stuerzt
 * zur Laufzeit mit "Invalid hook call" ab.
 */
export function BelowLimitToggle({ form, field, disabled }: BelowLimitToggleProps) {
  const [belowLimit, setBelowLimit] = useState<boolean>(() =>
    Boolean(getNestedValue(form.getValues(), field)),
  );

  const handleChange = useCallback(({ value }: { value: unknown }) => {
    setBelowLimit(Boolean(value));
  }, []);

  form.watch(field, handleChange);

  function handleClick(next: boolean) {
    setBelowLimit(next);
    form.setFieldValue(field, next, NO_REMOUNT);
  }

  return (
    <div className={classes.toggle}>
      <Tooltip
        label={belowLimit ? 'Unter der Bestimmungsgrenze' : 'Gemessener Wert'}
        events={{ hover: true, focus: true, touch: false }}
        position="top-start"
        withArrow
      >
        <button
          type="button"
          className={classes.button}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => handleClick(!belowLimit)}
          disabled={disabled}
          aria-label="Unter der Bestimmungsgrenze"
          aria-pressed={belowLimit}
        >
          {belowLimit ? '<' : '='}
        </button>
      </Tooltip>
    </div>
  );
}
